<template>
    <div class="aside-root" :class="{isMobile: isDisplayMobile, isMounted}">
        <aside
            ref="asideRef"
            class="aside-main flex"
            :class="{isFull: isDisplayModeFull, isReduced: isDisplayModeReduced, isMobile: isDisplayMobile, isMounted}"
        >
            <div class="aside-main_sticky">
                <WhiteLabel
                    v-if="selectedIdentity?.whitelabellingEnabled && selectedIdentity?.organizationName"
                    :company-name="selectedIdentity?.organizationName"
                    :company-logo="selectedIdentity?.organizationLogoUrl"
                    :is-reduced="isDisplayModeReduced"
                />
                <LogoWrapper v-else />
            </div>
            <div class="aside-main_actions flex">
                <NavigationTabsContainer />
                <div ref="bottomActionsRef" class="bottom-actions" data-testid="aside-menu-bottom-actions">
                    <div class="aside-main_actions_company-wrapper flex">
                        <Skeleton v-if="pending && !userStore.selectedIdentity" />
                        <template v-else>
                            <template v-if="isAdmin">
                                <NavigationTabItem v-if="isDisplayModeReduced" :tab="adminMenuNavItem" />
                                <NuxtLink v-else external to="/admin" target="_blank" class="aside-main_actions_company-wrapper_link">
                                    <div class="joy-pl-2 joy-pr-2">
                                        <VJoyButton
                                            variant="admin"
                                            size="small"
                                            class="aside-main_actions_company-wrapper_cta"
                                            data-testid="admin-button"
                                        >
                                            {{ $t('navbar.aside.tabs.title.admin') }}
                                        </VJoyButton>
                                    </div>
                                </NuxtLink>
                            </template>
                            <CompanySelector />
                        </template>
                    </div>
                    <LanguageIndicator class="aside-main_actions_language-indicator" />
                    <LogoWrapper v-if="selectedIdentity?.whitelabellingEnabled && selectedIdentity?.organizationName" is-small />
                </div>
            </div>
        </aside>
        <Transition name="fade-opacity">
            <div v-if="isDisplayMobile && isMobileNavBarOpened" class="aside-overlay" @click="handleCloseMobileNavBar"></div>
        </Transition>
    </div>
</template>

<script setup lang="tsx">
    import {computed, defineComponent, h, ref, watch} from 'vue';
    import {LogoWrapper, Skeleton, WhiteLabel} from '@navbar-logged/common/ui';
    import {LanguageIndicator} from '@navbar-logged/features/language-selector';
    import {useUserStore} from '@navbar-logged/features/user';
    import {useNavbarStateStore} from '@navbar-registry';
    import {storeToRefs} from 'pinia';
    import {useDebounceMount, useDisplayMobile} from '@malt/utils';
    import {VJoyButton, VJoyIconButton} from '@maltjoy/core-vue';
    import {useAsideMenuStore} from '../aside-menu.store';
    import type {NavigationTab} from '../aside-menu.types';
    import {useDisplayModeStore} from '../stores';
    import CompanySelector from './CompanySelector.vue';
    import NavigationTabItem from './NavigationTabItem.vue';
    import NavigationTabsContainer from './NavigationTabsContainer.vue';

    const asideMenuStore = useAsideMenuStore();
    const navbarState = useNavbarStateStore();
    const userStore = useUserStore();
    const displayModeStore = useDisplayModeStore();

    const {selectedIdentity} = storeToRefs(userStore);
    const {isMobileNavBarOpened} = storeToRefs(asideMenuStore);
    const {isDisplayModeFull, isDisplayModeReduced} = storeToRefs(displayModeStore);
    const {pending} = storeToRefs(navbarState);
    const isAdmin = computed<boolean>(() => !!userStore.user?.options.platformAdmin);
    const {isDisplayMobile} = useDisplayMobile();

    const asideRef = ref<HTMLDivElement>();
    const bottomActionsRef = ref<HTMLDivElement>();

    const {isMounted} = useDebounceMount(0, {mode: 'client'});

    function handleCloseMobileNavBar() {
        asideMenuStore.setMobileNavBarOpened(false);
    }

    const adminMenuNavItem = computed<NavigationTab>(() => ({
        id: 'Admin',
        title: 'Admin',
        url: {
            href: '/admin',
            blank: true,
        },
        icons: {
            mainIcon: defineComponent(() => () => h(<VJoyIconButton variant="admin" size="xsmall" icon="xml" />)),
        },
    }));

    watch(isDisplayMobile, (value) => {
        if (value) {
            setTimeout(() => {
                if (asideRef.value) {
                    asideRef.value.style.transitionProperty = 'transform';
                }
            }, 300);
        } else {
            setTimeout(() => {
                if (asideRef.value) {
                    asideRef.value.style.transitionProperty = 'width, transform';
                }
            }, 300);
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .aside-root {
        z-index: 998;
        $r: &;
        user-select: none;

        &::before {
            content: '';
            position: fixed;
            left: var(--aside-menu-left);
            width: var(--aside-menu-width);
            z-index: 25;
            height: calc(100vh - var(--header-menu-height)); // For old browsers compatibility
            box-shadow: 0px 4px 5px rgba(0, 0, 0, 0.1);
            top: calc(var(--header-menu-height));
        }

        &.isMounted::before {
            transition-duration: var(--joy-transition-duration-default);
            transition-timing-function: ease;
            transition-property: width, transform;
        }

        &.isMobile:before {
            display: none;
        }

        .aside-main {
            width: var(--aside-menu-width);
            height: 100vh; // For old browsers compatibility
            height: 100dvh;
            max-height: 100vh; // For old browsers compatibility
            max-height: 100dvh;
            max-width: 100vw;
            position: fixed;
            top: 0;
            left: 0;
            flex-flow: column nowrap;
            background-color: var(--joy-color-neutral-0);
            z-index: 25;
            transform: translateX(var(--aside-menu-left));
            $r: &;

            &_sticky {
                align-self: flex-start;
                background-color: var(--joy-color-neutral-0);
                z-index: 11;
                flex: 0 0 auto;
                width: 100%;
            }

            &_actions {
                flex-flow: column nowrap;
                justify-content: space-between;
                flex: 1 1 auto;
                padding-bottom: var(--joy-core-spacing-5);
                min-height: 0;

                &_language-indicator {
                    display: block;
                    @include mq.screen_sm(min) {
                        display: none;
                    }
                }

                .bottom-actions {
                    display: flex;
                    flex-flow: column wrap;
                }

                &_company-wrapper {
                    flex-direction: column;
                    gap: var(--joy-core-spacing-2);
                    padding: var(--joy-core-spacing-6) 0 var(--joy-core-spacing-2) 0;

                    &_cta {
                        width: 100%;
                    }
                }
            }

            &.isMounted {
                transition-duration: var(--joy-transition-duration-default);
                transition-timing-function: ease;
                transition-property: width, transform;
            }

            &.isReduced {
                .aside-main_sticky {
                    position: relative;
                }

                #{$r}_actions_company-wrapper {
                    align-items: center;

                    @media screen and (max-height: 700px) {
                        gap: var(--joy-core-spacing-2);
                    }
                }
            }
        }

        .aside-overlay {
            position: fixed;
            inset: 0;
            height: 100vh; // For old browsers compatibility
            height: 100dvh;
            width: 100vw;
            background-color: var(--joy-color-overlay);
            z-index: 24;
        }
    }
</style>
